<template>
  <div class="form-stats-card-container">
    <div class="card-header">
      <div class="card-title">
        {{ form.name }}
        <span v-if="lastUpdated && !$vuetify.display.smAndDown" class="pl-4 text-caption">{{ $t('i18n.lastUpdate') }} {{ lastUpdated.toLocaleTimeString('en-GB') }}</span>
      </div>
      <div class="card-options">
        <div class="link-container" @click="openAssociatedDevicesModal">
          {{ $t('i18n.viewAssociatedDevices') }}
        </div>
        <div class="export-stats-container px-2 cursor-pointer" @click="exportFormStatsReport">
          <v-icon icon="mdi-download" size="small"></v-icon>
          <v-tooltip activator="parent" location="start">
            {{ $t('i18n.downloadStatsReport') }}
          </v-tooltip>
        </div>
      </div>
    </div>
    <v-skeleton-loader v-if="loading" type="card"></v-skeleton-loader>
    <div v-else class="card-body-container">
      <!-- Vai renderizar os stats de cada form item usando o componente próprio -->
      <component v-for="(itemStatisticsObject, index) in formStatisticsObject.data" :key="index"
        :is="itemStatisticsObject.component_name"
        :formName="formStatisticsObject.form_name"
        :formItem="formItemsList[index]"
        :formItemIndex="index + 1"
        :stats="formStatisticsObject">
      </component>
    </div>
  </div>
  <v-dialog v-model="viewAssociatedDevicesIsActive" max-width="60%">
    <ViewFormAssociatedDevices :form="form" @close-modal="closeEditDeviceModal" />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Form, FormItem, FormStats, StatisticsReportRow, DateRange } from '@/utils/DataTypes'
import TextLabelStats from './stats_subcards/TextLabelStats.vue'
import MultiChoiceStats from '@/components/cards/stats_subcards/MultiChoiceStats.vue'
import NavigationStats from './stats_subcards/NavigationStats.vue'
import TextInputStats from './stats_subcards/TextInputStats.vue'
import RatingStats from './stats_subcards/RatingStats.vue'
import UnknownStats from './stats_subcards/UnknownStats.vue'
import ViewFormAssociatedDevices from '../modals/stats_modals/ViewFormAssociatedDevs.vue'
import { useRouter } from 'vue-router'
import { api, downloadCSV } from '@/utils/HelperFunctions'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'FormStatsCard',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  components: {
    TextLabelStats,
    MultiChoiceStats,
    NavigationStats,
    TextInputStats,
    RatingStats,
    UnknownStats,
    ViewFormAssociatedDevices
  },
  watch: {
    dateRange: {
      async handler () {
        this.loading = true
        await this.getFormItemStatistics()
        this.loading = false
      }
    }
  },
  async mounted () {
    this.startPolling()
    await this.getFormItemList()
    await this.getFormItemStatistics()
    await this.getFormStatsReportsData()
    this.lastUpdated = new Date()
    this.loading = false
  },
  beforeUnmount () {
    this.stopPolling()
  },
  props: {
    form: {
      type: Object as PropType<Form>,
      required: true,
      default: null
    },
    dateRange: {
      type: Object as PropType<DateRange | null>,
      required: false,
      default: null
    }
  },
  data () {
    return {
      formStatisticsObject: {} as FormStats,
      formItemsList: [] as Array<FormItem>,

      formStatisticsReport: [] as Array<StatisticsReportRow>,

      lastUpdated: null as Date | null,

      pollingIntervalFrequency: Number(process.env.VUE_APP_POLLING_INTERVAL) || 30000,
      pollingInterval: null as ReturnType<typeof setInterval> | null,

      viewAssociatedDevicesIsActive: false,
      loading: true
    }
  },
  methods: {
    async getFormStatsReportsData () {
      // Estatísticas que são exportadas sob a forma de ficheiros .csv
      try {
        if (!this.form.id) return
        const url = `/form-statistics-report/${this.form.id}`
        const getFormStatisticsResponse = await api(url, 'get')

        if (!getFormStatisticsResponse) return

        if (getFormStatisticsResponse.data.length > 0) {
          this.formStatisticsReport = getFormStatisticsResponse.data
          this.lastUpdated = new Date()
        }
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t export form statistics.')
      }
    },
    async getFormItemStatistics () {
      try {
        if (!this.form.id) return
        let url = `/statistics/${this.form.id}`
        if (this.dateRange) {
          url += `?startDate=${this.dateRange.startDate}&endDate=${this.dateRange.endDate}`
        }
        const getFormStatisticsResponse = await api(url, 'get')

        if (!getFormStatisticsResponse) return

        if (getFormStatisticsResponse.data.length > 0) {
          this.formStatisticsObject = getFormStatisticsResponse.data[0]
          this.lastUpdated = new Date()
        }
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get complete statistics of Form: ' + this.form.name)
      }
    },
    async getFormItemList () {
      try {
        if (!this.form.id) return

        const url = `/forms/${this.form.id}`
        const getFormResponse = await api(url, 'get')

        if (!getFormResponse) return

        const form = getFormResponse?.data?.[0]
        this.formItemsList = form.form_items
      } catch (error) {
        this.appStore.addAlert('error', 'Couldn\'t get data from Form: ' + this.form.name)
      }
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    startPolling () {
      this.stopPolling()
      this.pollingInterval = setInterval(async () => {
        await this.getFormItemStatistics()
        await this.getFormStatsReportsData()
      }, this.pollingIntervalFrequency)
    },
    stopPolling () {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    openAssociatedDevicesModal () {
      this.viewAssociatedDevicesIsActive = true
    },
    closeEditDeviceModal () {
      this.viewAssociatedDevicesIsActive = false
    },
    exportFormStatsReport () {
      try {
        const headers = [['Form name', 'Form item', 'Response', 'Submission date']]

        const rows = this.formStatisticsReport.map(row => [
          row.form_name,
          row.question_lang1,
          row.response_value,
          row.submission_date
        ])

        downloadCSV(headers, rows)
      } catch (error) {
        console.error('Error extracting form stats report: ', error)
        this.appStore.addAlert('error', 'Error extracting form responses.')
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../styles/colors.scss";

.form-stats-card-container {
  align-self: start;
  display: grid;
  grid-template-rows: auto auto;

  background-color: $white;
  border-radius: 15px;

  box-shadow: 2px 0 15px 0 rgba(0, 0, 0, 0.08);

  .card-header {

    display: grid;
    grid-template-columns: auto auto;
    justify-content: space-between;

    padding: 0.4em 0.4em 0.4em 1.0em;

    color: $white;
    background-color: $gorate-primary;
    border-radius: 15px 15px 0 0;

    .card-options {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: space-between;

      .link-container,
      .icon-container {
        cursor: pointer;
      }

      .link-container {
        text-decoration: underline;
        margin: 0 0.4em;
        /* text-wrap: nowrap; */
      }

      .icon-container {
        margin: 0 0.4em 0 1.0em;
      }
    }
  }

  .card-body-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    min-height: 10em;
    max-height: 35em;
    overflow-y: auto;

    .form-item-container {

      &:nth-child(3n + 1),
      &:nth-child(3n + 2) {
        position: relative;

        &::after {
          content: "";
          position: absolute;
          right: 0;
          top: 15%;
          width: 1px;
          height: 70%;
          border-right: 1px dashed $black;
        }

      }
    }
  }
}

@media (max-width: 960px) {
  .form-stats-card-container {
    .card-body-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(30rem, 100%), 1fr));

      .form-item-container {

        &:nth-of-type(1)::after,
        &:nth-of-type(2)::after {
          display: none;
        }
      }
    }
  }
}
</style>
